import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { X, Plus, Trash2, Building2, FileText, CreditCard, Share2, Copy, Mail, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewApplicationModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  // Lock body scroll when modal is open (critical for iOS mobile)
  useEffect(() => {
    const scrollY = window.scrollY;
    const html = document.documentElement;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      html.style.overflow = '';
      window.scrollTo(0, scrollY);
    };
  }, []);

  const [activeTab, setActiveTab] = useState('select-product');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [formData, setFormData] = useState({
    product_type: '',
    term_loan_data: null,
    sba_loan_data: null,
    line_of_credit_data: null
  });
  const [error, setError] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);

  const productTypes = [
    {
      value: 'term_loan',
      label: 'Term Loan Application',
      description: 'Traditional business term loan with fixed payments',
      icon: Building2,
      minAmount: 25000,
      maxAmount: 500000
    },
    {
      value: 'sba_loan', 
      label: 'SBA Loan Application',
      description: 'SBA-backed loan with favorable terms ($25K - $5M)',
      icon: FileText,
      minAmount: 25000,
      maxAmount: 350000
    },
    {
      value: 'line_of_credit',
      label: 'Line of Credit Application',
      description: 'Flexible credit line for working capital',
      icon: CreditCard,
      minAmount: 10000,
      maxAmount: 250000
    }
  ];

  const handleProductSelect = (productType) => {
    setSelectedProduct(productType);
    setFormData({ ...formData, product_type: productType });
    setActiveTab('application-form');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_type) {
      setError('Please select a product type');
      return;
    }

    // Validate required fields based on product type
    let isValid = true;
    let missingFields = [];

    if (selectedProduct === 'term_loan' && formData.term_loan_data) {
      const required = ['first_name', 'last_name', 'email_address', 'phone_number', 'social_security_number', 'home_address', 'ownership_percentage', 'legal_corporate_name', 'industry', 'office_space', 'business_type', 'business_location_type', 'state_of_incorporation', 'business_start_date', 'business_address', 'gross_annual_sales', 'funding_amount_requested', 'use_of_funds_description', 'application_type'];
      
      for (let field of required) {
        if (!formData.term_loan_data[field]) {
          missingFields.push(field.replace('_', ' '));
          isValid = false;
        }
      }
    } else if (selectedProduct === 'sba_loan' && formData.sba_loan_data) {
      const required = ['loan_requested_amount', 'has_existing_debt', 'total_monthly_sales', 'business_legal_name', 'legal_entity_type', 'business_start_date', 'federal_tax_id', 'business_phone_number', 'business_address', 'naics_code_or_nature', 'first_name', 'last_name', 'date_of_birth', 'social_security_number', 'mobile_phone_number', 'email_address', 'home_address'];
      
      for (let field of required) {
        if (formData.sba_loan_data[field] === '' || formData.sba_loan_data[field] === null || formData.sba_loan_data[field] === undefined) {
          missingFields.push(field.replace('_', ' '));
          isValid = false;
        }
      }
    } else if (selectedProduct === 'line_of_credit' && formData.line_of_credit_data) {
      const required = ['owner_first_name', 'owner_last_name', 'owner_home_address', 'owner_social_security_number', 'owner_email_address', 'owner_mobile_phone_number', 'business_name', 'business_zip_code', 'business_industry', 'years_in_business'];
      
      for (let field of required) {
        if (!formData.line_of_credit_data[field]) {
          missingFields.push(field.replace('_', ' '));
          isValid = false;
        }
      }
    }

    if (!isValid) {
      setError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await axios.post(`${BACKEND_URL}/api/applications`, formData);
      
      onSuccess();
      onClose();
      alert('✅ Application created successfully! Documents can be uploaded later via Communications Center.');
    } catch (err) {
      console.error('Error creating application:', err);
      setError(err.response?.data?.detail || 'Failed to create application');
    } finally {
      setLoading(false);
    }
  };

  const getBusinessNameFromForm = () => {
    const data = formData.term_loan_data || formData.sba_loan_data || formData.line_of_credit_data || 
                 formData.term_loan_3_5yr_data || formData.term_loc_hybrid_data || formData.term_loan_10yr_data || {};
    return data.legal_corporate_name || data.business_legal_name || data.business_name || '';
  };

  const handleShareToBorrower = async (sendEmail = false) => {
    const bizName = getBusinessNameFromForm();
    if (!bizName.trim()) {
      setError('Please enter the business name before sharing the link.');
      return;
    }
    setShareLoading(true);
    setError('');
    try {
      const saveData = { ...formData, status: 'incomplete' };
      const res = await axios.post(`${BACKEND_URL}/api/applications`, saveData);
      const appId = res.data.id;

      const shareRes = await axios.post(`${BACKEND_URL}/api/applications/${appId}/borrower-share`, {
        application_id: appId,
        borrower_email: sendEmail ? shareEmail : null,
        send_email: sendEmail,
      });

      const portalUrl = window.location.origin;
      const link = `${portalUrl}/shared/borrower/${shareRes.data.token}`;
      setShareLink(link);

      if (sendEmail) {
        alert(`Link emailed to ${shareEmail}`);
        onSuccess();
        onClose();
      }
    } catch (err) {
      console.error('Error sharing:', err);
      setError(err.response?.data?.detail || 'Failed to create share link');
    } finally {
      setShareLoading(false);
    }
  };

  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50" style={{ height: '100dvh', height: '100vh' }}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      
      {/* Modal - takes full screen */}
      <div 
        className="absolute inset-0 flex flex-col bg-white"
        onClick={e => e.stopPropagation()}
      >
        {/* Fixed Header - 64px */}
        <div className="h-16 flex-shrink-0 border-b flex items-center justify-between px-4 sm:px-6 bg-white z-10">
          <h2 className="text-xl font-semibold text-slate-800">Create New Application</h2>
          <div className="flex items-center gap-2">
            {selectedProduct && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowShareDialog(true)}
                className="border-teal-500 text-teal-600 hover:bg-teal-50 font-semibold gap-1.5"
                data-testid="share-to-borrower-btn"
              >
                <Share2 className="h-4 w-4" />
                Share
              </Button>
            )}
            <button onClick={onClose} disabled={loading} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200" data-testid="close-new-app-modal">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Content - explicit height calculation */}
        <div 
          className="overflow-y-auto bg-white modal-scroll-area"
          data-testid="modal-scroll-area"
          style={{ 
            height: 'calc(100dvh - 64px)', 
            height: 'calc(100vh - 64px)',
            WebkitOverflowScrolling: 'touch', 
            overscrollBehavior: 'contain' 
          }}
        >
          <div className="p-4 sm:p-6 max-w-6xl mx-auto w-full pb-8">

        {showShareDialog && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60]" data-testid="share-borrower-dialog">
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-slate-800 mb-1">Share with Borrower</h3>
              <p className="text-sm text-slate-500 mb-4">The application will be saved and a link will be created for the borrower to complete it.</p>

              {shareLink ? (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-green-700">Link created successfully!</p>
                  <div className="flex items-center gap-2">
                    <input value={shareLink} readOnly className="flex-1 text-xs border rounded-lg px-3 py-2 bg-slate-50" />
                    <Button size="sm" onClick={copyShareLink} className="gap-1.5 flex-shrink-0" data-testid="copy-borrower-link">
                      {shareCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                      {shareCopied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={() => { setShowShareDialog(false); onSuccess(); onClose(); }} data-testid="share-done-btn">Done</Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium mb-1 block">Email the link to borrower</Label>
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="borrower@email.com"
                        value={shareEmail}
                        onChange={(e) => setShareEmail(e.target.value)}
                        data-testid="borrower-email-input"
                      />
                      <Button
                        onClick={() => handleShareToBorrower(true)}
                        disabled={!shareEmail.trim() || shareLoading}
                        className="bg-[#0E333F] hover:bg-[#081120] gap-1.5 flex-shrink-0"
                        data-testid="email-borrower-btn"
                      >
                        <Mail className="h-4 w-4" />
                        Send
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-slate-400">OR</span>
                    <div className="flex-1 h-px bg-slate-200" />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handleShareToBorrower(false)}
                    disabled={shareLoading}
                    className="w-full gap-1.5"
                    data-testid="get-copy-link-btn"
                  >
                    <Copy className="h-4 w-4" />
                    Generate Copy Link
                  </Button>
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={() => setShowShareDialog(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="p-6">
          {error && (
            <Alert className="mb-6 border-red-200 bg-red-50">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="select-product">Select Product</TabsTrigger>
              <TabsTrigger value="application-form" disabled={!selectedProduct}>
                Application Details
              </TabsTrigger>
            </TabsList>

            <TabsContent value="select-product" className="mt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {productTypes.map((product) => {
                  const IconComponent = product.icon;
                  return (
                    <Card 
                      key={product.value}
                      className={`cursor-pointer transition-all ${
                        selectedProduct === product.value 
                          ? 'ring-2 ring-slate-500 border-slate-300' 
                          : 'hover:shadow-md'
                      }`}
                      onClick={() => handleProductSelect(product.value)}
                    >
                      <CardHeader className="text-center">
                        <div className="flex justify-center mb-3">
                          <IconComponent className="h-12 w-12 text-slate-600" />
                        </div>
                        <CardTitle className="text-lg">{product.label}</CardTitle>
                        <CardDescription>{product.description}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="text-sm text-slate-600 text-center">
                          <p>Amount: ${product.minAmount.toLocaleString()} - ${product.maxAmount.toLocaleString()}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>

            <TabsContent value="application-form" className="mt-6">
              {selectedProduct === 'term_loan' && (
                <TermLoanForm formData={formData} setFormData={setFormData} />
              )}
              {selectedProduct === 'sba_loan' && (
                <SBALoanForm formData={formData} setFormData={setFormData} />
              )}
              {selectedProduct === 'line_of_credit' && (
                <LineOfCreditForm formData={formData} setFormData={setFormData} />
              )}
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-3 mt-8 pt-6 border-t">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            {selectedProduct && (
              <Button 
                onClick={handleSubmit}
                disabled={loading}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {loading ? 'Creating...' : 'Create Application'}
              </Button>
            )}
          </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

// Term Loan Application Form
const TermLoanForm = ({ formData, setFormData }) => {
  const [data, setData] = useState(formData.term_loan_data || {
    // Primary Borrower Information
    first_name: '',
    last_name: '',
    email_address: '',
    phone_number: '',
    social_security_number: '',
    home_address: '',
    ownership_percentage: '',
    
    // Business Information
    legal_corporate_name: '',
    company_name_dba: '',
    industry: '',
    office_space: '',
    business_type: '',
    business_location_type: '',
    state_of_incorporation: '',
    business_start_date: '',
    business_website: '',
    business_address: '',
    
    // Financial Information
    gross_annual_sales: '',
    funding_amount_requested: '',
    use_of_funds_description: '',
    application_type: ''
  });

  useEffect(() => {
    setFormData({
      ...formData,
      term_loan_data: data
    });
  }, [data]);

  const handleChange = (field, value) => {
    setData({ ...data, [field]: value });
  };

  return (
    <div className="space-y-8">
      <Alert>
        <AlertDescription>
          All fields are required. Documents can be uploaded later via Communications Center.
        </AlertDescription>
      </Alert>

      {/* Primary Borrower Information */}
      <Card>
        <CardHeader>
          <CardTitle>Primary Borrower Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={data.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={data.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="email_address">Email Address *</Label>
              <Input
                id="email_address"
                type="email"
                value={data.email_address}
                onChange={(e) => handleChange('email_address', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="phone_number">Phone Number *</Label>
              <Input
                id="phone_number"
                value={data.phone_number}
                onChange={(e) => handleChange('phone_number', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="social_security_number">Social Security Number *</Label>
              <Input
                id="social_security_number"
                value={data.social_security_number}
                onChange={(e) => handleChange('social_security_number', e.target.value)}
                placeholder="XXX-XX-XXXX"
                required
              />
            </div>
            <div>
              <Label htmlFor="ownership_percentage">Percentage of Ownership in Business *</Label>
              <Input
                id="ownership_percentage"
                type="number"
                min="0"
                max="100"
                value={data.ownership_percentage}
                onChange={(e) => handleChange('ownership_percentage', e.target.value)}
                placeholder="0-100"
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="home_address">Home Address *</Label>
            <Input
              id="home_address"
              value={data.home_address}
              onChange={(e) => handleChange('home_address', e.target.value)}
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card>
        <CardHeader>
          <CardTitle>Business Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="legal_corporate_name">Legal / Corporate Name *</Label>
              <Input
                id="legal_corporate_name"
                value={data.legal_corporate_name}
                onChange={(e) => handleChange('legal_corporate_name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="company_name_dba">Company Name (DBA) if any</Label>
              <Input
                id="company_name_dba"
                value={data.company_name_dba}
                onChange={(e) => handleChange('company_name_dba', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="industry">Industry *</Label>
              <Input
                id="industry"
                value={data.industry}
                onChange={(e) => handleChange('industry', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="office_space">Office Space *</Label>
              <Select value={data.office_space} onValueChange={(value) => handleChange('office_space', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Owned">Owned</SelectItem>
                  <SelectItem value="Rented">Rented</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="business_type">Business Type *</Label>
              <Select value={data.business_type} onValueChange={(value) => handleChange('business_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select business type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sole Proprietorship">Sole Proprietorship</SelectItem>
                  <SelectItem value="C Corporation">C Corporation</SelectItem>
                  <SelectItem value="S Corporation">S Corporation</SelectItem>
                  <SelectItem value="LLP">LLP</SelectItem>
                  <SelectItem value="Partnership">Partnership</SelectItem>
                  <SelectItem value="LLC">LLC</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="business_location_type">Business Location Type *</Label>
              <Select value={data.business_location_type} onValueChange={(value) => handleChange('business_location_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select location type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="state_of_incorporation">State of Incorporation *</Label>
              <Input
                id="state_of_incorporation"
                value={data.state_of_incorporation}
                onChange={(e) => handleChange('state_of_incorporation', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="business_start_date">Business Start Date *</Label>
              <Input
                id="business_start_date"
                type="date"
                value={data.business_start_date}
                onChange={(e) => handleChange('business_start_date', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="business_website">Business Website</Label>
              <Input
                id="business_website"
                value={data.business_website}
                onChange={(e) => handleChange('business_website', e.target.value)}
                placeholder="https://www.example.com"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="business_address">Business Address *</Label>
            <Input
              id="business_address"
              value={data.business_address}
              onChange={(e) => handleChange('business_address', e.target.value)}
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Financial Information */}
      <Card>
        <CardHeader>
          <CardTitle>Financial Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="gross_annual_sales">Gross Annual Sales *</Label>
              <Input
                id="gross_annual_sales"
                type="number"
                value={data.gross_annual_sales}
                onChange={(e) => handleChange('gross_annual_sales', e.target.value)}
                placeholder="Enter annual sales amount"
                required
              />
            </div>
            <div>
              <Label htmlFor="funding_amount_requested">Funding Amount Requested *</Label>
              <Input
                id="funding_amount_requested"
                type="number"
                value={data.funding_amount_requested}
                onChange={(e) => handleChange('funding_amount_requested', e.target.value)}
                placeholder="Enter requested amount"
                required
              />
            </div>
            <div>
              <Label htmlFor="application_type">Application Type *</Label>
              <Select value={data.application_type} onValueChange={(value) => handleChange('application_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select application type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Credit Pre-Approval Only">Credit Pre-Approval Only</SelectItem>
                  <SelectItem value="Full Package Submission">Full Package Submission</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="use_of_funds_description">Use of Funds Description *</Label>
            <Textarea
              id="use_of_funds_description"
              value={data.use_of_funds_description}
              onChange={(e) => handleChange('use_of_funds_description', e.target.value)}
              placeholder="Describe how the loan will be used"
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Required Documents Info */}
      <Card>
        <CardHeader>
          <CardTitle>Required Documents (Full Package)</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              <strong>The following documents will be required:</strong>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>Last 6 months bank statements</li>
                <li>Last 2 years business tax returns (or P&L if last year not available)</li>
                <li>Last 1 year personal tax returns for all owners over 20%</li>
                <li>Government ID for all owners over 20%</li>
                <li>A General Business Questionnaire</li>
                <li>Business Debt Schedule if they are refinancing any debts</li>
              </ul>
              <p className="mt-2 text-sm">Documents can be uploaded after application submission via Communications Center.</p>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

// SBA Loan Application Form
const SBALoanForm = ({ formData, setFormData }) => {
  const [data, setData] = useState(formData.sba_loan_data || {
    // Loan Request
    loan_requested_amount: '',
    has_existing_debt: '',
    refinance_existing_debt: '',
    total_monthly_sales: '',
    
    // Business Information
    business_legal_name: '',
    doing_business_as: '',
    legal_entity_type: '',
    business_start_date: '',
    federal_tax_id: '',
    business_phone_number: '',
    business_address: '',
    naics_code_or_nature: '',
    number_of_employees: '',
    
    // Primary Applicant
    first_name: '',
    last_name: '',
    first_last_name: '', // legacy, kept in sync for back-compat
    date_of_birth: '',
    social_security_number: '',
    mobile_phone_number: '',
    email_address: '',
    home_address: '',
    
    // Personal Financial Statement (simplified)
    assets_cash: '',
    assets_home: '',
    assets_automobile: '',
    assets_investment_accounts: '',
    assets_other: '',
    liabilities_mortgage: '',
    liabilities_credit_cards: '',
    liabilities_auto_loans: '',
    liabilities_other: '',
    
    // Additional Owners
    additional_owners: [],
    
    // Additional Notes
    additional_notes: '',

    // Income
    annual_income: ''
  });
  
  const [showExistingDebtFields, setShowExistingDebtFields] = useState(false);

  useEffect(() => {
    setFormData({
      ...formData,
      sba_loan_data: data
    });
  }, [data]);

  const handleChange = (field, value) => {
    setData({ ...data, [field]: value });
    
    if (field === 'has_existing_debt') {
      setShowExistingDebtFields(value === 'Yes');
      if (value === 'No') {
        setData(prev => ({ ...prev, refinance_existing_debt: 'No' }));
      }
    }
  };

  const addOwner = () => {
    const newOwner = {
      id: Date.now(),
      first_name: '',
      last_name: '',
      first_last_name: '', // legacy
      date_of_birth: '',
      social_security_number: '',
      mobile_phone_number: '',
      email_address: '',
      home_address: '',
      percentage_of_ownership: ''
    };
    setData({ ...data, additional_owners: [...data.additional_owners, newOwner] });
  };

  const removeOwner = (id) => {
    setData({ 
      ...data, 
      additional_owners: data.additional_owners.filter(owner => owner.id !== id) 
    });
  };

  const updateOwner = (id, field, value) => {
    setData({
      ...data,
      additional_owners: data.additional_owners.map(owner =>
        owner.id === id ? { ...owner, [field]: value } : owner
      )
    });
  };

  return (
    <div className="space-y-8">
      <Alert>
        <AlertDescription>
          All fields marked with * are required. Documents can be uploaded later via Communications Center.
        </AlertDescription>
      </Alert>

      {/* Loan Request */}
      <Card>
        <CardHeader>
          <CardTitle>Loan Request</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="loan_requested_amount">Loan Requested Amount ($25K to $5M) *</Label>
              <Input
                id="loan_requested_amount"
                type="number"
                min="25000"
                max="5000000"
                value={data.loan_requested_amount}
                onChange={(e) => handleChange('loan_requested_amount', e.target.value)}
                placeholder="Enter amount between $25,000 - $5,000,000"
                required
              />
            </div>
            <div>
              <Label htmlFor="total_monthly_sales">Total Monthly Sales *</Label>
              <Input
                id="total_monthly_sales"
                type="number"
                value={data.total_monthly_sales}
                onChange={(e) => handleChange('total_monthly_sales', e.target.value)}
                placeholder="Enter monthly sales"
                required
              />
            </div>
            <div>
              <Label htmlFor="has_existing_debt">Does the business have existing debt? *</Label>
              <Select value={data.has_existing_debt} onValueChange={(value) => handleChange('has_existing_debt', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select option" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {showExistingDebtFields && (
              <div>
                <Label htmlFor="refinance_existing_debt">Are you looking to refinance any of that debt? *</Label>
                <Select value={data.refinance_existing_debt} onValueChange={(value) => handleChange('refinance_existing_debt', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Required Documents Info */}
      <Card>
        <CardHeader>
          <CardTitle>Required Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              <strong>The following documents will be required:</strong>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>Last 2 years business tax returns</li>
                <li>Last 1 year personal tax returns for all owners with more than 20% ownership</li>
                <li>Business debt schedule</li>
                <li>Last 6 months business bank statements</li>
                <li>Government identification for all owners &gt;20%</li>
                <li>P&L for the most recent tax year only if the tax return for that year is not filed yet because it is on extension (this replaces that year's return)</li>
              </ul>
              <p className="mt-2 text-sm">Documents can be uploaded after application submission via Communications Center.</p>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card>
        <CardHeader>
          <CardTitle>Business Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="business_legal_name">Business Legal Name *</Label>
              <Input
                id="business_legal_name"
                value={data.business_legal_name}
                onChange={(e) => handleChange('business_legal_name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="doing_business_as">Doing Business As (DBA)</Label>
              <Input
                id="doing_business_as"
                value={data.doing_business_as}
                onChange={(e) => handleChange('doing_business_as', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="legal_entity_type">Legal Entity Type *</Label>
              <Select value={data.legal_entity_type} onValueChange={(value) => handleChange('legal_entity_type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select entity type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sole Proprietorship">Sole Proprietorship</SelectItem>
                  <SelectItem value="Partnership">Partnership</SelectItem>
                  <SelectItem value="LLC">LLC</SelectItem>
                  <SelectItem value="Corporation">Corporation</SelectItem>
                  <SelectItem value="S-Corporation">S-Corporation</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="business_start_date">Business Start Date *</Label>
              <Input
                id="business_start_date"
                type="date"
                value={data.business_start_date}
                onChange={(e) => handleChange('business_start_date', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="federal_tax_id">Federal Tax ID (EIN) *</Label>
              <Input
                id="federal_tax_id"
                value={data.federal_tax_id}
                onChange={(e) => handleChange('federal_tax_id', e.target.value)}
                placeholder="XX-XXXXXXX"
                required
              />
            </div>
            <div>
              <Label htmlFor="business_phone_number">Business Phone Number *</Label>
              <Input
                id="business_phone_number"
                value={data.business_phone_number}
                onChange={(e) => handleChange('business_phone_number', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="naics_code_or_nature">NAICS Code or Nature of Business *</Label>
              <Input
                id="naics_code_or_nature"
                value={data.naics_code_or_nature}
                onChange={(e) => handleChange('naics_code_or_nature', e.target.value)}
                placeholder="Enter NAICS code or describe business"
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="business_address">Business Address *</Label>
            <Input
              id="business_address"
              value={data.business_address}
              onChange={(e) => handleChange('business_address', e.target.value)}
              required
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="number_of_employees">Number of Employees</Label>
              <Input
                id="number_of_employees"
                type="number"
                min="0"
                value={data.number_of_employees}
                onChange={(e) => handleChange('number_of_employees', e.target.value)}
                placeholder="e.g., 12"
                data-testid="sba-number-of-employees"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Primary Applicant */}
      <Card>
        <CardHeader>
          <CardTitle>Primary Applicant Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                value={data.first_name}
                onChange={(e) => handleChange('first_name', e.target.value)}
                required
                data-testid="sba-first-name"
              />
            </div>
            <div>
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                value={data.last_name}
                onChange={(e) => handleChange('last_name', e.target.value)}
                required
                data-testid="sba-last-name"
              />
            </div>
            <div>
              <Label htmlFor="date_of_birth">Date of Birth *</Label>
              <Input
                id="date_of_birth"
                type="date"
                value={data.date_of_birth}
                onChange={(e) => handleChange('date_of_birth', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="social_security_number">Social Security Number *</Label>
              <Input
                id="social_security_number"
                value={data.social_security_number}
                onChange={(e) => handleChange('social_security_number', e.target.value)}
                placeholder="XXX-XX-XXXX"
                required
              />
            </div>
            <div>
              <Label htmlFor="mobile_phone_number">Mobile Phone Number *</Label>
              <Input
                id="mobile_phone_number"
                value={data.mobile_phone_number}
                onChange={(e) => handleChange('mobile_phone_number', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="email_address">Email Address *</Label>
              <Input
                id="email_address"
                type="email"
                value={data.email_address}
                onChange={(e) => handleChange('email_address', e.target.value)}
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="home_address">Home Address *</Label>
            <Input
              id="home_address"
              value={data.home_address}
              onChange={(e) => handleChange('home_address', e.target.value)}
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Personal Financial Statement */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Financial Statement (Primary Applicant)</CardTitle>
          <CardDescription>Provide approximate values for your personal assets and liabilities</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3">Assets</h4>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="assets_cash">Cash & Bank Accounts</Label>
                  <Input
                    id="assets_cash"
                    type="number"
                    value={data.assets_cash}
                    onChange={(e) => handleChange('assets_cash', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="assets_home">Home (Market Value)</Label>
                  <Input
                    id="assets_home"
                    type="number"
                    value={data.assets_home}
                    onChange={(e) => handleChange('assets_home', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="assets_automobile">Automobile</Label>
                  <Input
                    id="assets_automobile"
                    type="number"
                    value={data.assets_automobile}
                    onChange={(e) => handleChange('assets_automobile', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="assets_investment_accounts">Investments</Label>
                  <Input
                    id="assets_investment_accounts"
                    type="number"
                    value={data.assets_investment_accounts}
                    onChange={(e) => handleChange('assets_investment_accounts', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="assets_other">Other Assets</Label>
                  <Input
                    id="assets_other"
                    type="number"
                    value={data.assets_other}
                    onChange={(e) => handleChange('assets_other', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3">Liabilities</h4>
              <div className="space-y-3">
                <div>
                  <Label htmlFor="liabilities_mortgage">Mortgage</Label>
                  <Input
                    id="liabilities_mortgage"
                    type="number"
                    value={data.liabilities_mortgage}
                    onChange={(e) => handleChange('liabilities_mortgage', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="liabilities_credit_cards">Credit Cards</Label>
                  <Input
                    id="liabilities_credit_cards"
                    type="number"
                    value={data.liabilities_credit_cards}
                    onChange={(e) => handleChange('liabilities_credit_cards', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="liabilities_auto_loans">Auto Loans</Label>
                  <Input
                    id="liabilities_auto_loans"
                    type="number"
                    value={data.liabilities_auto_loans}
                    onChange={(e) => handleChange('liabilities_auto_loans', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label htmlFor="liabilities_other">Other Liabilities</Label>
                  <Input
                    id="liabilities_other"
                    type="number"
                    value={data.liabilities_other}
                    onChange={(e) => handleChange('liabilities_other', e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Owners */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Owners (20% or more ownership)</CardTitle>
          <CardDescription>Add any additional owners with 20% or more ownership in the business</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {data.additional_owners.map((owner, index) => (
            <div key={owner.id} className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-medium">Owner {index + 1}</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeOwner(owner.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={owner.first_name || ''}
                    onChange={(e) => updateOwner(owner.id, 'first_name', e.target.value)}
                    data-testid={`sba-owner-${index}-first-name`}
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={owner.last_name || ''}
                    onChange={(e) => updateOwner(owner.id, 'last_name', e.target.value)}
                    data-testid={`sba-owner-${index}-last-name`}
                  />
                </div>
                <div>
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={owner.date_of_birth}
                    onChange={(e) => updateOwner(owner.id, 'date_of_birth', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Social Security Number</Label>
                  <Input
                    value={owner.social_security_number}
                    onChange={(e) => updateOwner(owner.id, 'social_security_number', e.target.value)}
                    placeholder="XXX-XX-XXXX"
                  />
                </div>
                <div>
                  <Label>Mobile Phone Number</Label>
                  <Input
                    value={owner.mobile_phone_number}
                    onChange={(e) => updateOwner(owner.id, 'mobile_phone_number', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={owner.email_address}
                    onChange={(e) => updateOwner(owner.id, 'email_address', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Ownership Percentage</Label>
                  <Input
                    type="number"
                    min="20"
                    max="100"
                    value={owner.percentage_of_ownership}
                    onChange={(e) => updateOwner(owner.id, 'percentage_of_ownership', e.target.value)}
                    placeholder="20-100"
                  />
                </div>
              </div>
              <div>
                <Label>Home Address</Label>
                <Input
                  value={owner.home_address}
                  onChange={(e) => updateOwner(owner.id, 'home_address', e.target.value)}
                />
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={addOwner}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Additional Owner
          </Button>
        </CardContent>
      </Card>

      {/* Additional Notes */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Notes</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="additional_notes">Additional Information (Optional)</Label>
            <Textarea
              id="additional_notes"
              value={data.additional_notes}
              onChange={(e) => handleChange('additional_notes', e.target.value)}
              placeholder="Any additional information you'd like to provide..."
              rows={4}
            />
          </div>
          <div className="mt-4">
            <Label htmlFor="annual_income">Annual Income</Label>
            <Input
              id="annual_income"
              type="number"
              value={data.annual_income}
              onChange={(e) => handleChange('annual_income', e.target.value)}
              placeholder="Annual income"
              data-testid="sba-annual-income"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Line of Credit Application Form
const LineOfCreditForm = ({ formData, setFormData }) => {
  const [data, setData] = useState(formData.line_of_credit_data || {
    owner_first_name: '',
    owner_last_name: '',
    owner_name: '', // legacy, kept for back-compat
    owner_home_address: '',
    owner_social_security_number: '',
    owner_email_address: '',
    owner_mobile_phone_number: '',
    business_name: '',
    business_zip_code: '',
    business_industry: '',
    years_in_business: ''
  });

  useEffect(() => {
    setFormData({
      ...formData,
      line_of_credit_data: data
    });
  }, [data]);

  const handleChange = (field, value) => {
    setData({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6">
      <Alert>
        <AlertDescription>
          All fields are required. Documents can be uploaded later via Communications Center.
        </AlertDescription>
      </Alert>

      {/* All Fields in One Card */}
      <Card>
        <CardHeader>
          <CardTitle>Line of Credit Application</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner_first_name">Owner&apos;s First Name *</Label>
              <Input
                id="owner_first_name"
                value={data.owner_first_name || ''}
                onChange={(e) => handleChange('owner_first_name', e.target.value)}
                required
              />
            </div>
            <div>
              <Label htmlFor="owner_last_name">Owner&apos;s Last Name *</Label>
              <Input
                id="owner_last_name"
                value={data.owner_last_name || ''}
                onChange={(e) => handleChange('owner_last_name', e.target.value)}
                required
              />
            </div>

            {/* Owner's Social Security Number */}
            <div>
              <Label htmlFor="owner_social_security_number">Owner's Social Security Number *</Label>
              <Input
                id="owner_social_security_number"
                value={data.owner_social_security_number}
                onChange={(e) => handleChange('owner_social_security_number', e.target.value)}
                placeholder="XXX-XX-XXXX"
                required
              />
            </div>

            {/* Owner's Email Address */}
            <div>
              <Label htmlFor="owner_email_address">Owner's Email Address *</Label>
              <Input
                id="owner_email_address"
                type="email"
                value={data.owner_email_address}
                onChange={(e) => handleChange('owner_email_address', e.target.value)}
                required
              />
            </div>

            {/* Owner's Mobile Phone Number */}
            <div>
              <Label htmlFor="owner_mobile_phone_number">Owner's Mobile Phone Number *</Label>
              <Input
                id="owner_mobile_phone_number"
                value={data.owner_mobile_phone_number}
                onChange={(e) => handleChange('owner_mobile_phone_number', e.target.value)}
                required
              />
            </div>

            {/* Business Name */}
            <div>
              <Label htmlFor="business_name">Business Name *</Label>
              <Input
                id="business_name"
                value={data.business_name}
                onChange={(e) => handleChange('business_name', e.target.value)}
                required
              />
            </div>

            {/* Business Zip Code */}
            <div>
              <Label htmlFor="business_zip_code">Business Zip Code *</Label>
              <Input
                id="business_zip_code"
                value={data.business_zip_code}
                onChange={(e) => handleChange('business_zip_code', e.target.value)}
                placeholder="12345"
                required
              />
            </div>

            {/* Business Industry */}
            <div>
              <Label htmlFor="business_industry">Business Industry *</Label>
              <Input
                id="business_industry"
                value={data.business_industry}
                onChange={(e) => handleChange('business_industry', e.target.value)}
                required
              />
            </div>

            {/* Years in Business */}
            <div>
              <Label htmlFor="years_in_business">Years in Business *</Label>
              <Select value={data.years_in_business} onValueChange={(value) => handleChange('years_in_business', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0–1 year">0–1 year</SelectItem>
                  <SelectItem value="1–2 years">1–2 years</SelectItem>
                  <SelectItem value="2–5 years">2–5 years</SelectItem>
                  <SelectItem value="5+ years">5+ years</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            {/* Owner's Home Address - Full width */}
            <Label htmlFor="owner_home_address">Owner's Home Address *</Label>
            <Input
              id="owner_home_address"
              value={data.owner_home_address}
              onChange={(e) => handleChange('owner_home_address', e.target.value)}
              required
            />
          </div>
        </CardContent>
      </Card>

      {/* Required Documents Info */}
      <Card>
        <CardHeader>
          <CardTitle>Required Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert>
            <AlertDescription>
              <strong>The following document will be required:</strong>
              <ul className="mt-2 list-disc list-inside">
                <li>Last 3 months business bank statements</li>
              </ul>
              <p className="mt-2 text-sm">Documents can be uploaded after application submission via Communications Center.</p>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
};

export default NewApplicationModal;